import { DonorReportOutput } from 'gql/types';
import { TFunction } from 'i18next';
import { ReportColumnConfig } from 'modules/reports/types';
import { formatDateSafe } from 'modules/reports/utils/formatDateSafe';
import { formatSafeValue } from 'modules/reports/utils/formatSafeValue';

import { formatDonorNotes } from './utils/formatDonorNotes';
import { getEffectiveRetiredDate } from './utils/getEffectiveRetiredDate';
import { getRequiresAppointment } from './utils/getRequiresAppointment';

export const getDonorReportColumns = (
  t: TFunction
): ReportColumnConfig<DonorReportOutput>[] => [
  // --- Basic Info ---
  {
    key: 'id',
    label: t('id'),
    getValue: (item) => item.id || '',
  },
  {
    key: 'shortId',
    label: t('shortId'),
    getValue: (item) => item.shortId || '',
  },
  {
    key: 'name',
    label: t('name'),
    getValue: (item) => item.name || '',
  },

  // --- Owner ---
  {
    key: 'ownerLastName',
    label: t('ownerLastName'),
    getValue: (item) => item.user?.lastName || '',
  },
  {
    key: 'ownerId',
    label: t('ownerId'),
    getValue: (item) => item.user?.id || '',
  },

  // --- Location ---
  {
    key: 'location',
    label: t('location'),
    getValue: (item) => item.location || '',
  },
  {
    key: 'region',
    label: t('region'),
    getValue: (item) => item.region || '',
  },

  // --- Appointment ---
  {
    key: 'requiresAppointment',
    label: t('requiresAppointment'),
    getValue: (item) =>
      getRequiresAppointment(item.isDaycare, item.canRegularlyAttendDaycare)
        ? 'Yes'
        : 'No',
  },

  // --- Physical Info ---
  {
    key: 'species',
    label: t('species'),
    getValue: (item) => item.species || '',
  },
  {
    key: 'breed',
    label: t('breed'),
    getValue: (item) => item.breed || '',
  },
  {
    key: 'color',
    label: t('color'),
    getValue: (item) => item.color || '',
  },
  {
    key: 'gender',
    label: t('gender'),
    getValue: (item) => item.gender || '',
  },
  {
    key: 'age',
    label: t('age'),
    getValue: (item) => formatSafeValue(item.age, ''),
  },
  {
    key: 'weight',
    label: t('weight'),
    getValue: (item) => formatSafeValue(item.weight, ''),
  },
  {
    key: 'neutered',
    label: t('neutered'),
    getValue: (item) => formatSafeValue(item.neutered, ''),
  },

  // --- Blood Info ---
  {
    key: 'bloodType',
    label: t('bloodType'),
    getValue: (item) => item.bloodType || '',
  },

  // --- Status ---
  {
    key: 'status',
    label: t('status'),
    getValue: (item) => item.status || '',
  },

  // --- Dates ---
  {
    key: 'becameReservedDonorDate',
    label: t('registrationDate'),
    getValue: (item) => formatDateSafe(item.becameReservedDonorDate),
  },
  {
    key: 'rabiesDate',
    label: t('rabiesDate'),
    getValue: (item) => formatDateSafe(item.rabiesDate),
  },
  {
    key: 'distemperDate',
    label: t('distemperDate'),
    getValue: (item) => formatDateSafe(item.distemperDate),
  },
  {
    key: 'retiredDate',
    label: t('retiredDate'),
    getValue: (item) =>
      formatDateSafe(
        getEffectiveRetiredDate(
          item.retiredDate,
          item.status,
          item.donorStatusUpdatedAt
        )
      ),
  },
  {
    key: 'retiredReason',
    label: t('retiredReason'),
    getValue: (item) => item.retiredReason || '',
  },
  {
    key: 'retiredReasonComment',
    label: t('retiredReasonComment'),
    getValue: (item) => item.retiredReasonComment || '',
  },
  {
    key: 'nextDonationDate',
    label: t('nextDonationDate'),
    getValue: (item) => formatDateSafe(item.nextDonationDate),
  },
  {
    key: 'labWorkDue',
    label: t('labWorkDue'),
    getValue: (item) => formatDateSafe(item.labWorkDue),
  },
  {
    key: 'dvmExamDue',
    label: t('dvmExamDue'),
    getValue: (item) => formatDateSafe(item.dvmExamDue),
  },

  // --- Medical ---
  {
    key: 'primaryVeterinarian',
    label: t('primaryVeterinarian'),
    getValue: (item) => item.primaryVeterinarian || '',
  },
  {
    key: 'dvmExamPerformedBy',
    label: t('dvmExamPerformedBy'),
    getValue: (item) => item.dvmExamPerformedBy || '',
  },
  {
    key: 'microchipNumber',
    label: t('microchipNumber'),
    getValue: (item) => item.microchipNumber || '',
  },

  // --- Donation Info ---
  {
    key: 'totalCollected',
    label: t('totalCollected'),
    getValue: (item) => formatSafeValue(item.totalCollected, ''),
  },
  {
    key: 'approvedDonationML',
    label: t('approvedDonationML'),
    getValue: (item) => formatSafeValue(item.approvedDonationML, ''),
  },
  {
    key: 'donationMLApprovedAmount',
    label: t('donationMLApprovedAmount'),
    getValue: (item) => formatSafeValue(item.donationMLApprovedAmount, ''),
  },
  {
    key: 'donationFrequency',
    label: t('donationFrequency'),
    getValue: (item) => formatSafeValue(item.donationFrequency, ''),
  },

  // --- Additional ---
  {
    key: 'acceptSimparicaTrio',
    label: t('acceptSimparicaTrio'),
    getValue: (item) => formatSafeValue(item.acceptSimparicaTrio, ''),
  },

  // --- Notes ---
  {
    key: 'notes',
    label: t('notes'),
    getValue: (item) => formatDonorNotes(item.notes || []),
  },
];
